import { nanoid } from 'nanoid';
import type { ForwardedRef, ComponentPropsWithRef } from 'react';
import { forwardRef, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { InputError } from '@atoms/InputError/InputError';

import { Label } from 'components/ui/atoms/Label';
import { Wrap } from 'components/ui/atoms/Wrap';

export interface InputFieldProps extends ComponentPropsWithRef<'input'> {
  /**
   * String with class names that will be attached to the input default classes
   */
  inputClassName?: string;
  /**
   * Error message
   */
  errorMessage?: string;
  /**
   * Label to show before the input
   */
  label?: string;
  /**
   * Data test id
   */
  'data-testid'?: string;
}

/**
 * Simple text input
 */
export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      errorMessage,
      children,
      className,
      label,
      disabled,
      inputClassName = '',
      'data-testid': dataTestId,
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const idRef = useRef<string>(id || nanoid());
    const inputProps = {
      ref,
      id: idRef.current,
      className: twMerge(
        'rounded-2xl h-12 p-4 w-full border cool-shadow',
        !disabled && 'group-hover:shadow-plain-lg focus:shadow-plain-lg',
        inputClassName
      ),
      'aria-invalid': !!errorMessage,
      'aria-errormessage': errorMessage ? `${idRef.current}-error` : undefined
    };

    return (
      <Wrap
        if={!!label}
        with={Label}
        wrapperProps={{
          text: label,
          className,
          disabled
        }}
      >
        <div className={twMerge('relative', !label && className)}>
          <input
            {...props}
            {...inputProps}
            disabled={disabled}
            data-testid={dataTestId}
          />
          {children}
          <InputError
            errorMessage={errorMessage}
            id={inputProps['aria-errormessage']}
            data-testid={dataTestId ? `${dataTestId}-error` : undefined}
          />
        </div>
      </Wrap>
    );
  }
);

InputField.displayName = 'InputField';
